/* eslint-disable no-console */
import NotificationSubscriptions from '../api/notificationSubscription';
import auth from '../api/auth';
import { useAlert } from 'dashboard/composables';

export const verifyServiceWorkerExistence = (callback = () => {}) => {
  if (!('serviceWorker' in navigator)) {
    // Service Worker isn't supported on this browser, disable or hide UI.
    return;
  }

  if (!('PushManager' in window)) {
    // Push isn't supported on this browser, disable or hide UI.
    return;
  }

  navigator.serviceWorker
    .register('/sw.js')
    .then(registration => callback(registration))
    .catch(registrationError => {
      // eslint-disable-next-line
      console.log('SW registration failed: ', registrationError);
    });
};

export const hasPushPermissions = () => {
  if ('Notification' in window) {
    return Notification.permission === 'granted';
  }
  return false;
};

const generateKeys = str =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const getPushSubscriptionPayload = subscription => ({
  subscription_type: 'browser_push',
  subscription_attributes: {
    endpoint: subscription.endpoint,
    p256dh: generateKeys(subscription.getKey('p256dh')),
    auth: generateKeys(subscription.getKey('auth')),
  },
});

export const sendRegistrationToServer = subscription => {
  if (auth.hasAuthCookie()) {
    return NotificationSubscriptions.create(
      getPushSubscriptionPayload(subscription)
    );
  }
  return null;
};

export const registerSubscription = (
  onSuccess = () => {},
  onFailure = () => {}
) => {
  if (!window.vhConfig.vapidPublicKey) {
    // VAPID public key is not configured on the server, so the browser cannot
    // subscribe to web push. Surface this instead of silently failing — otherwise
    // the toggle in profile settings will appear to reset after refresh.
    console.error(
      'Push subscription registration aborted: VAPID public key is missing. ' +
        'Configure VAPID_KEYS on the server (see VapidService).'
    );
    useAlert(
      'Web push is not configured on this server. Please contact your administrator.'
    );
    onFailure();
    return;
  }
  navigator.serviceWorker.ready
    .then(serviceWorkerRegistration =>
      serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: window.vhConfig.vapidPublicKey,
      })
    )
    .then(sendRegistrationToServer)
    .then(() => {
      onSuccess();
    })
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error('Push subscription registration failed:', error);
      useAlert('This browser does not support desktop notification');
      onFailure();
    });
};

export const requestPushPermissions = ({ onSuccess, onFailure = () => {} }) => {
  if (!('Notification' in window)) {
    // eslint-disable-next-line no-console
    console.warn('Notification is not supported');
    useAlert('This browser does not support desktop notification');
    onFailure();
  } else if (Notification.permission === 'granted') {
    registerSubscription(onSuccess, onFailure);
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission(permission => {
      if (permission === 'granted') {
        registerSubscription(onSuccess, onFailure);
      } else {
        onFailure();
      }
    });
  } else {
    // permission was previously denied — there's no way to re-prompt programmatically.
    useAlert(
      'Push notifications are blocked in browser settings. Please enable them and try again.'
    );
    onFailure();
  }
};
